import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  FaBolt,
  FaSyncAlt,
  FaBrain,
  FaRobot,
  FaSpider,
  FaCheckCircle,
  FaExclamationTriangle,
  FaPause,
  FaBroom,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  TechButtonGhost,
  Empty,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import friendlyError from '../../utils/friendlyError';
import { getAiLiveNow, cleanupStalledProcesses } from '../../services/aiLiveService';
import { getCronSchedule } from '../../services/cronScheduleService';
import PlatformGraph3D from './PlatformGraph3D';

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0, 180, 216, 0.5); }
  70%  { box-shadow: 0 0 0 8px rgba(0, 180, 216, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 180, 216, 0); }
`;

const ActivityList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const ActivityRow = styled.li`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.85rem;
  align-items: center;
  padding: 0.7rem 0.9rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 5px;
  transition: border-color 0.18s ease;

  &:hover { border-color: rgba(0, 180, 216, 0.3); }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${accent};
    animation: ${pulse} 1.6s infinite;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .label {
    font-size: 0.92rem;
    color: var(--text-primary);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;

    svg { color: var(--text-disabled); font-size: 0.78rem; }
  }

  .meta {
    font-size: 0.77rem;
    color: var(--text-secondary);
    line-height: 1.35;

    a { color: ${accent}; text-decoration: none; }
    a:hover { text-decoration: underline; }
  }

  .right {
    text-align: right;
    font-family: ${mono};
    font-size: 0.7rem;
    color: var(--text-disabled);
    letter-spacing: 0.1em;
    white-space: nowrap;
  }
`;

const QueueGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.5rem;
`;

const QueueCard = styled.li`
  padding: 0.55rem 0.75rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  font-size: 0.8rem;

  a {
    color: var(--text-primary);
    text-decoration: none;
    font-weight: 500;
    &:hover { color: ${accent}; }
  }

  .meta {
    margin-top: 4px;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }

  .bar {
    margin-top: 5px;
    height: 3px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 2px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    background: ${accent};
    transition: width 0.4s ease;
  }
`;

const KIND_ICON = {
  auto_run: FaBrain,
  agent_search: FaRobot,
  scrape_run: FaSpider,
};

function fmtElapsed(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

function fmtCountdown(ms) {
  if (ms == null) return '—';
  if (ms <= 0) return 'ya mismo';
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function fmtAbs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-AR', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

const ScheduleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th, td {
    text-align: left;
    padding: 0.55rem 0.75rem;
    border-bottom: 1px solid ${borderSoft};
  }

  th {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    font-weight: 500;
    background: ${panelDeep};
  }

  td.label a {
    color: var(--text-primary);
    text-decoration: none;
    font-weight: 500;
    &:hover { color: ${accent}; }
  }

  td.mono {
    font-family: ${mono};
    font-size: 0.78rem;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  td.countdown {
    font-family: ${mono};
    color: ${accent};
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  tbody tr:hover { background: rgba(0, 180, 216, 0.04); }
`;

const REFRESH_MS = 5000;

const AiLive = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [, setTick] = useState(0); // re-render por segundo para countdown
  const mountedRef = useRef(true);

  const cleanupStalled = async () => {
    setCleaning(true);
    try {
      await cleanupStalledProcesses();
      await load();
    } catch {
      // el refresh siguiente lo reintenta; no bloqueamos la UI
    } finally {
      if (mountedRef.current) setCleaning(false);
    }
  };

  const load = async () => {
    try {
      const fresh = await getAiLiveNow();
      if (!mountedRef.current) return;
      setData(fresh);
      setError(null);
      setLastFetchAt(Date.now());
    } catch (err) {
      if (!mountedRef.current) return;
      setError(friendlyError(err));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const loadSchedule = async () => {
    try {
      const s = await getCronSchedule();
      if (mountedRef.current) setSchedule(s);
    } catch {
      // silent — la sección simplemente no se renderiza
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    load();
    loadSchedule();
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  // Tick por segundo para que el countdown se descuente; refresca schedule
  // cuando algún cron pasa su next_run_at.
  useEffect(() => {
    const t = setInterval(() => {
      setTick((v) => v + 1);
      const crons = schedule?.crons || [];
      if (crons.some((c) => c.next_run_at && new Date(c.next_run_at).getTime() <= Date.now())) {
        loadSchedule();
      }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  const allActivities = data?.activities || [];
  const activities = allActivities.filter((a) => !a.stale);
  const stalled = allActivities.filter((a) => a.stale);
  const queued = data?.queued_loops || [];
  const stats = data?.stats || {};
  const secondsSinceFetch = lastFetchAt ? Math.floor((Date.now() - lastFetchAt) / 1000) : null;

  return (
    <TechShell>
      <TechPageHeader
        title="IA en vivo"
        subtitle="Procesos de inteligencia que están corriendo ahora mismo en la plataforma"
        dossier="AI://LIVE/01"
        actions={
          <>
            <TechButtonGhost
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {autoRefresh ? <FaPause /> : <FaSyncAlt />}
              {autoRefresh ? 'Pausar auto-refresh' : 'Reanudar'}
            </TechButtonGhost>
            <TechButtonGhost
              type="button"
              onClick={load}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FaSyncAlt /> Refrescar ya
            </TechButtonGhost>
          </>
        }
      >
        <StatusPill>
          {autoRefresh
            ? <>Auto-refresh <strong>cada {REFRESH_MS / 1000}s</strong></>
            : <>Auto-refresh <strong>pausado</strong></>}
        </StatusPill>
        <StatusPill>
          Activas <strong>{activities.length}</strong>
        </StatusPill>
        {secondsSinceFetch != null && (
          <StatusPill>Última lectura <strong>hace {secondsSinceFetch}s</strong></StatusPill>
        )}
      </TechPageHeader>

      <StatStrip min="170px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaBolt /> En curso</StatLabel>
          <StatValue>{activities.length}</StatValue>
          <StatSub>investigaciones + scrapers</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCheckCircle /> Completadas 24h</StatLabel>
          <StatValue>{stats.runs_done_24h ?? 0}</StatValue>
          <StatSub>auto-runs OK</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaExclamationTriangle /> Fallidas 24h</StatLabel>
          <StatValue>{stats.runs_failed_24h ?? 0}</StatValue>
          <StatSub>requieren revisión</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaSpider /> Notas indexadas 24h</StatLabel>
          <StatValue>{stats.media_articles_24h ?? 0}</StatValue>
          <StatSub>medios + búsquedas</StatSub>
        </StatTile>
      </StatStrip>

      {error && <ErrorBox>No se pudo cargar · {error}</ErrorBox>}

      <PlatformGraph3D />

      {schedule?.crons?.length > 0 && (
        <TechSection
          title={`Próximos monitoreos diarios · ${schedule.crons.length}`}
          style={{ marginBottom: '1.25rem' }}
        >
          <ScheduleTable>
            <thead>
              <tr>
                <th>Fuente</th>
                <th>Cron</th>
                <th>Última corrida</th>
                <th>Próxima corrida</th>
                <th>Faltan</th>
              </tr>
            </thead>
            <tbody>
              {schedule.crons.map((c) => {
                const ms = c.next_run_at
                  ? new Date(c.next_run_at).getTime() - Date.now()
                  : null;
                return (
                  <tr key={c.key}>
                    <td className="label">
                      <Link to={c.where}>{c.label}</Link>
                    </td>
                    <td className="mono">{c.schedule} {c.timezone}</td>
                    <td className="mono">{fmtAbs(c.last_run_at)}</td>
                    <td className="mono">{fmtAbs(c.next_run_at)}</td>
                    <td className="countdown">{fmtCountdown(ms)}</td>
                  </tr>
                );
              })}
            </tbody>
          </ScheduleTable>
        </TechSection>
      )}

      <TechSection title={`Trabajando ahora · ${activities.length}`}>
        {loading && !data ? (
          <Empty>Cargando…</Empty>
        ) : activities.length === 0 ? (
          <Empty>
            La IA está en reposo: en este momento no hay ninguna investigación ni
            monitoreo corriendo. El sistema se despierta solo con los monitoreos diarios
            (arriba) y cuando alguien del equipo lanza una investigación.
          </Empty>
        ) : (
          <ActivityList>
            {activities.map((a) => {
              const Icon = KIND_ICON[a.kind] || FaRobot;
              return (
                <ActivityRow key={a.id}>
                  <span className="dot" />
                  <div className="body">
                    <span className="label">
                      <Icon /> {a.label}
                    </span>
                    <span className="meta">
                      en{' '}
                      <Link to={a.where}>{a.where_label}</Link>
                      {a.what ? ` · ${a.what}` : ''}
                      {a.triggered_by ? ` · disparado por ${a.triggered_by}` : ''}
                    </span>
                  </div>
                  <span className="right">{fmtElapsed(a.elapsed_ms)}</span>
                </ActivityRow>
              );
            })}
          </ActivityList>
        )}
      </TechSection>

      {stalled.length > 0 && (
        <TechSection
          title={`Procesos interrumpidos · ${stalled.length}`}
          style={{ marginTop: '1.25rem' }}
          action={
            <TechButtonGhost
              type="button"
              onClick={cleanupStalled}
              disabled={cleaning}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FaBroom /> {cleaning ? 'Limpiando…' : 'Marcar como finalizados'}
            </TechButtonGhost>
          }
        >
          <Empty style={{ textAlign: 'left', marginBottom: '0.75rem' }}>
            Estos procesos quedaron colgados (llevan más de 6 horas sin terminar) y ya
            no están corriendo de verdad. No cuentan como actividad y podés cerrarlos.
          </Empty>
          <ActivityList>
            {stalled.map((a) => {
              const Icon = KIND_ICON[a.kind] || FaRobot;
              return (
                <ActivityRow key={a.id} style={{ borderLeftColor: 'var(--alert-error)', opacity: 0.8 }}>
                  <span className="dot" style={{ background: 'var(--alert-error)', animation: 'none' }} />
                  <div className="body">
                    <span className="label">
                      <Icon /> {a.label}
                    </span>
                    <span className="meta">
                      en <Link to={a.where}>{a.where_label}</Link>
                      {a.what ? ` · ${a.what}` : ''}
                    </span>
                  </div>
                  <span className="right">detenido hace {fmtElapsed(a.elapsed_ms)}</span>
                </ActivityRow>
              );
            })}
          </ActivityList>
        </TechSection>
      )}

      {queued.length > 0 && (
        <TechSection
          title={`Cola de loops · ${queued.length} dossiers en cola`}
          style={{ marginTop: '1.25rem' }}
        >
          <QueueGrid>
            {queued.map((q) => {
              const pct = Math.min(100, Math.round((q.cycle / Math.max(1, q.max)) * 100));
              return (
                <QueueCard key={q.slug}>
                  <Link to={`/investigations/${q.slug}`}>{q.title}</Link>
                  <div className="meta">
                    ciclo {q.cycle}/{q.max}
                  </div>
                  <div className="bar">
                    <div className="bar-fill" style={{ width: pct + '%' }} />
                  </div>
                </QueueCard>
              );
            })}
          </QueueGrid>
        </TechSection>
      )}
    </TechShell>
  );
};

export default AiLive;
